<template>
  <div class="wt-container">
    <p class="introduction-text text-center">
      The annual green, blue and grey water footprint of your commodity, {{nameText}}, per {{perText}}.
    </p>
    <div class="d-flex align-items-center">
      <CountryOrBasinSelector class="me-2"/>
      <div class="form-check me-3 mb-2">
        <input type="checkbox" v-model="grouped" id="grouped" class="form-check-input">
        <label for="grouped" class="form-check-label">Group by sector and crop/material</label>
      </div>
      <div v-if="assessment.scenario.quantity > 0" class="form-check mb-2">
        <input type="checkbox" v-model="wfxVisible" id="show-wfx" class="form-check-input">
        <label for="show-wfx" class="form-check-label">Show columns per output unit</label>
      </div>
    </div>
    <Table :columns="columns" :rows="rows" :fileName="fileName"></Table>
    <transition name="fade">
      <div v-if="loading" class="lottie">
        <Lottie></Lottie>
      </div>
    </transition>
  </div>
</template>

<script>
import Lottie from "@/components/shared/Lottie.vue";
import CountryOrBasinSelector from "@/components/shared/CountryOrBasinSelector.vue"
import Table from "@/components/shared/Table.vue";
import { getFunctionGetPercentageString, getFunctionGetWFString, getWFString } from "@/services/data-tools.js";
import { getCountry, getCrop, scenarioCountryCrops, scenarioCountryCropsLoader, scenarioCrops, scenarioCropsLoader, scenariosLoader, quantityUnits } from "@/services/entity-model.js";
import { assessment } from "@/services/assessment-model.js";

export default {
  name: "ProductionAccountTableCt",
  components: {
    Lottie,
    CountryOrBasinSelector,
    Table
  },
  data() {
    return {
      scenariosLoader: scenariosLoader,
      scenarioCountryCropsLoader: scenarioCountryCropsLoader,
      scenarioCountryCrops: scenarioCountryCrops,
      scenarioCropsLoader: scenarioCropsLoader,
      scenarioCrops: scenarioCrops,
      assessment: assessment,
      grouped: false,
      wfxVisible: false
    };
  },
  mounted() {
    if (assessment.countriesOrBasins.length == 0) {
      this.grouped = true;
    } else if (assessment.scenario.quantity > 0) {
      this.wfxVisible = true;
    }
  },
  computed: {
    nameText() {
      return assessment.scenario.name + (!assessment.countriesOrBasins.length ? "" : ", in the selected country");
    },
    perText() {
      if (this.grouped) {
        return "crop, material or sector";
      }
      return "crop or material" + (!assessment.countriesOrBasins.length ? ", and country" : "");
    },
    columns() {
      let columns = [];

      if (this.grouped) {
        columns.push({ key: "cropID",           label: "Crop/Material or Sector", lookupFunction: val => getCrop(val)?.name || "Locations", cssFunction: row => row.cropLevel ? `ps-${row.cropLevel - 1}` : ""});
        columns.push({ key: "countryISO2Codes", label: "Countries",                 dataFunction: val => "<small>" + val.replace(/,/g, ", ") + "</small>"});
      } else {
        columns.push({ key: "cropId",           label: "Crop or Material",        lookupFunction: val => getCrop(val).name});
        if (assessment.countriesOrBasins.length == 0) {
          columns.push({ key: "countryId",      label: "Country",                 lookupFunction: val => getCountry(val).name});
        }
      }

      columns = columns.concat([
        { key: "greenWF", label: "Green WF (m³/yr)", dataFunction: val => getWFString(val.toPrecision(2), "yr"), classes: ["group left-row"] },
        { key: "blueWF",  label: "Blue WF (m³/yr)",  dataFunction: val => getWFString(val.toPrecision(2), "yr"), classes: ["group"] },
        { key: "greyWF",  label: "Grey WF (m³/yr)",  dataFunction: val => getWFString(val.toPrecision(2), "yr"), classes: ["group"] },
        { key: "wf",      label: "Total WF (m³/yr)", dataFunction: val => getWFString(val.toPrecision(2), "yr"), classes: ["group right-row"] }
      ]);

      if (this.wfxVisible) {
        const unit = quantityUnits.find(unit => unit.unitId == assessment.scenario.unitID);
        columns = columns.concat([
          { key: "whiteSpace", label: "",                                                                                   classes: ["white-space"] },
          { key: "greenWFx",   label: "Green WF (m³/" + unit.short + ")", dataFunction: getFunctionGetWFString(unit.short), classes: ["group left-row"] },
          { key: "blueWFx",    label: "Blue WF (m³/" + unit.short + ")",  dataFunction: getFunctionGetWFString(unit.short), classes: ["group"] },
          { key: "greyWFx",    label: "Grey WF (m³/" + unit.short + ")",  dataFunction: getFunctionGetWFString(unit.short), classes: ["group"] },
          { key: "wfx",        label: "Total WF (m³/" + unit.short + ")", dataFunction: getFunctionGetWFString(unit.short), classes: ["group right-row"] }
        ]);
      }

      columns = columns.concat([
        { key: "greenWFFraction", label: "% of Green WF", dataFunction: getFunctionGetPercentageString(1) },
        { key: "blueWFFraction",  label: "% of Blue WF",  dataFunction: getFunctionGetPercentageString(1) },
        { key: "greyWFFraction",  label: "% of Grey WF",  dataFunction: getFunctionGetPercentageString(1) },
        { key: "wfFraction",      label: "% of Total WF", dataFunction: getFunctionGetPercentageString(1) }
      ]);

      return columns;
    },
    rows() {
      if (this.grouped) {
        return this.scenarioCrops;
      }
      if (assessment.countriesOrBasins.length > 0) {
        return this.scenarioCountryCrops.filter(scenarioCountryCrop => scenarioCountryCrop.countryId == assessment.countriesOrBasins[0].countryID);
      }
      return this.scenarioCountryCrops;
    },
    fileName() {
      return "wf-accounting-" + assessment.scenario.name + (assessment.countriesOrBasins[0]? ("-" + assessment.countriesOrBasins[0].name): "") + (this.grouped? "-grouped": "");
    },
    loading() {
      return this.scenariosLoader.status.loading ||
        (this.grouped && this.scenarioCropsLoader.status.loading) ||
        (!this.grouped && this.scenarioCountryCropsLoader.status.loading);
    }
  },
  watch: {
    "assessment.countriesOrBasins"(val) {
      if (val.length > 0) {
        this.grouped = false;
      }
    },
    grouped(val) {
      if (val) {
        assessment.setCountriesOrBasins([]);
        this.wfxVisible = false;
      }
    },
    wfxVisible(val) {
      if (val) {
        this.grouped = false;
      }
    }
  }
}
</script>
